import { API_BASE_URL } from '../../constants/appConstants';
import { authenticatedRequest } from '../authService';

const SUPERADMIN_BASE_URL = `${API_BASE_URL}/superadmin`;

/**
 * Fetch paginated list of proformas with search and filtering
 * @param {Object} params - Query parameters
 * @param {string} params.search - Search term for title, proforma_number, client, client_email
 * @param {number} params.owner - Owner user ID filter
 * @param {number} params.created_by - Created by user ID filter
 * @param {string} params.status - Proforma status filter
 * @param {string} params.date_from - Start date filter (YYYY-MM-DD)
 * @param {string} params.date_to - End date filter (YYYY-MM-DD)
 * @param {string} params.sort - Sort order (asc/desc)
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.page_size - Items per page (default: 10)
 * @returns {Promise<Object>} Response with proformas list and pagination info
 */
export const fetchProformas = async (params = {}) => {
  const {
    search = '',
    owner = '',
    created_by = '',
    status = '',
    date_from = '',
    date_to = '',
    sort = 'desc',
    page = 1,
    page_size = 10
  } = params;

  // Build query string
  const queryParams = new URLSearchParams();
  if (search.trim()) queryParams.append('search', search.trim());
  if (owner) queryParams.append('owner', owner.toString());
  if (created_by) queryParams.append('created_by', created_by.toString());
  if (status) queryParams.append('status', status);
  if (date_from) queryParams.append('date_from', date_from);
  if (date_to) queryParams.append('date_to', date_to);
  if (sort) queryParams.append('sort', sort);
  queryParams.append('page', page.toString());
  queryParams.append('page_size', page_size.toString());

  const url = `${SUPERADMIN_BASE_URL}/proformas/?${queryParams.toString()}`;

  try {
    const response = await authenticatedRequest(url, {
      method: 'GET',
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch proformas');
    }

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching proformas:', error);
    throw new Error(error.message || 'Failed to fetch proformas');
  }
};

/**
 * Fetch detailed information for a specific proforma
 * @param {number} proformaId - Proforma ID
 * @returns {Promise<Object>} Response with proforma details and attachments
 */
export const fetchProformaDetails = async (proformaId) => {
  if (!proformaId) {
    throw new Error('Proforma ID is required');
  }

  const url = `${SUPERADMIN_BASE_URL}/proformas/${proformaId}/`;

  try {
    const response = await authenticatedRequest(url, {
      method: 'GET',
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch proforma details');
    }

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching proforma details:', error);
    throw new Error(error.message || 'Failed to fetch proforma details');
  }
};